import { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { DashboardShell } from "@/components/Shell";
import { LayoutDashboard, Coins, MapPin, Users, BarChart3 } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  return (
    <DashboardShell role="admin">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Administration</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3">
          <LayoutDashboard className="w-9 h-9 text-[#EBB300]" /> Übersicht
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-cards">
        <Stat label="Gesamtsumme" value={stats ? formatEUR(stats.total_amount) : "—"} icon={Coins} accent="#EBB300" />
        <Stat label="Trinkgelder" value={stats?.total_tips ?? "—"} icon={BarChart3} accent="#22C55E" />
        <Stat label="Locations" value={stats?.locations ?? "—"} icon={MapPin} accent="#E05A33" />
        <Stat label="Mitarbeiter" value={stats?.staff ?? "—"} icon={Users} accent="#A1A1AA" />
      </div>

      {/* Per-location */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#121216] border border-white/5 rounded-2xl p-6">
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4">Trinkgelder pro Location</h2>
          <div className="space-y-3" data-testid="admin-per-location">
            {(stats?.per_location || []).length === 0 && <div className="text-sm text-[#A1A1AA]">Keine Daten.</div>}
            {(stats?.per_location || []).map((l) => {
              const max = Math.max(...(stats?.per_location || []).map((x) => x.total), 1);
              const pct = (l.total / max) * 100;
              return (
                <div key={l.location_id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold">{l.name}</span>
                    <span className="text-[#EBB300] font-bold">{formatEUR(l.total)}</span>
                  </div>
                  <div className="h-2 bg-[#0A0A0C] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#EBB300] to-[#FAD02C]" style={{ width: `${pct}%` }}></div>
                  </div>
                  <div className="text-xs text-[#A1A1AA] mt-1">{l.count} Trinkgelder</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#121216] border border-white/5 rounded-2xl p-6">
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4">Letzte 30 Tage</h2>
          <div className="flex items-end gap-1 h-40" data-testid="admin-timeseries">
            {(stats?.timeseries || []).map((d, i) => {
              const max = Math.max(...(stats?.timeseries || []).map((x) => x.amount), 1);
              const h = (d.amount / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full bg-[#EBB300]/30 rounded-t group-hover:bg-[#EBB300] transition-colors" style={{ height: `${h}%` }}></div>
                </div>
              );
            })}
          </div>
          {stats?.timeseries?.length === 0 && <div className="text-sm text-[#A1A1AA]">Noch keine Daten.</div>}
          <div className="text-xs text-[#A1A1AA] mt-3">Tägliche Summe in EUR</div>
        </div>
      </div>
    </DashboardShell>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[#121216] border border-white/5 rounded-2xl p-5">
      <Icon className="w-5 h-5 mb-3" style={{ color: accent }} />
      <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">{label}</div>
      <div className="text-3xl font-black tracking-tight mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
